import path from "path";

import MiniCssExtractPlugin from "mini-css-extract-plugin";
import webpack, { Configuration } from "webpack";

import { BuildCache, FormBuildCacheOptions } from "./CacheAssetsPlugin";

type BuildMode = "production" | "development";

interface CreateWebpackConfigurationVariantOptions {
    gfv: string;
    resourcesHash: string;
    formPath: string;
    mode?: BuildMode;
    isLocalDev?: boolean;
    resourcesDirectoryPath: string;
    buildCacheOptions?: FormBuildCacheOptions;
}

async function createConfigVariant(options: CreateWebpackConfigurationVariantOptions): Promise<Configuration> {
    const { gfv } = options;

    const fileNameSuffix = options.mode === "production" ? ".min" : "";

    const formPath = options.formPath;
    const buildCache =
        options.buildCacheOptions != undefined
            ? new BuildCache(options.buildCacheOptions.farmDirectory, options.buildCacheOptions.mode)
            : undefined;

    const buildCacheWebpackPlugin =
        buildCache != undefined && options.buildCacheOptions != undefined
            ? await buildCache.createWebpackPlugin(options.buildCacheOptions)
            : undefined;

    const devtoolOption: Configuration["devtool"] =
        options.mode === "production" ? "hidden-source-map" : options.isLocalDev ? "inline-source-map" : "source-map";

    const threadLoaderOptions = {
        workers: 4,
        workerParallelJobs: 10,
        workerNodeArgs: ["--max-old-space-size=3072"],
    };

    const babelLoaderRules = [];

    if (!options.isLocalDev) {
        const loader = await import("thread-loader");
        loader.warmup(threadLoaderOptions, ["babel-loader", "@babel/preset-env", "@babel/preset-react"]);

        babelLoaderRules.push({
            loader: require.resolve("thread-loader"),
            options: threadLoaderOptions,
        });
    }

    return {
        mode: options.mode,
        target: ["web", "es5"],
        context: formPath,
        output: {
            filename: `public/scripts/${gfv}${fileNameSuffix}.js`,
            path: options.resourcesDirectoryPath,
            publicPath: "/static/",
        },
        resolve: {
            alias: {
                "variables.less": path.resolve(path.join(__dirname, "variables.less")),
                variables: path.resolve(path.join(__dirname, "variables.less")),
                Resources: path.join(formPath, "form", "resources"),
            },
            extensions: [".js", ".jsx"],
            modules: ["node_modules", path.resolve(__dirname, "node_modules")],
        },
        entry: [path.join(formPath, "form", "./web")],
        devtool: devtoolOption,
        cache:
            options.mode === "development"
                ? {
                      type: "memory",
                      maxGenerations: 5,
                  }
                : false,
        module: {
            rules: [
                {
                    test: /\.less$/,
                    use: [
                        {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            loader: MiniCssExtractPlugin.loader as any,
                            options: { publicPath: "../../" },
                        },
                        {
                            loader: require.resolve("css-loader"),
                            options: {
                                modules: {
                                    localIdentName: "[hash:base64:5]",
                                },
                            },
                        },
                        {
                            loader: require.resolve("postcss-loader"),
                            options: {
                                postcssOptions: {
                                    ident: "postcss",
                                    plugins: [
                                        // eslint-disable-next-line @typescript-eslint/no-require-imports
                                        require("autoprefixer")({
                                            // Просто дефолтные значение из текущего (1.7.7) browserslist
                                            overrideBrowserslist: "> 1%, last 2 versions, Firefox ESR",
                                        }),
                                    ],
                                },
                            },
                        },
                        {
                            loader: require.resolve("less-loader"),
                        },
                    ],
                },
                {
                    test: /\.jsx?$/,
                    oneOf: [
                        {
                            test: /(kcXmlValidationConfigurator|kcXmlAutoCalculationConfigurator|dataDeclaration)\.js$/,
                            type: "javascript/auto",
                        },
                        {
                            use: [
                                ...babelLoaderRules,
                                {
                                    loader: require.resolve("babel-loader"),
                                    options: {
                                        babelrc: false,
                                        configFile: false,
                                        presets: [
                                            require.resolve("@babel/preset-react"),
                                            [
                                                require.resolve("@babel/preset-env"),
                                                {
                                                    targets: {
                                                        browsers: [">0.5%", "last 2 versions", "not dead", "not IE 11"],
                                                    },
                                                    loose: true,
                                                    useBuiltIns: "usage",
                                                    corejs: 3,
                                                },
                                            ],
                                        ],
                                        plugins: [
                                            [require.resolve("@babel/plugin-proposal-decorators"), { legacy: true }],
                                            [
                                                require.resolve("@babel/plugin-transform-class-properties"),
                                                { loose: true },
                                            ],
                                            require.resolve("@babel/plugin-proposal-export-default-from"),
                                        ],
                                        cacheDirectory: true,
                                    },
                                },
                            ],
                            resolve: {
                                fullySpecified: false,
                            },
                            exclude: [/node_modules/],
                        },
                    ],
                },
                {
                    test: /\.(png|svg)$/,
                    loader: require.resolve("file-loader"),
                    options: { name: "public/images/[hash].[ext]" },
                },
                {
                    test: /\.(ttf|eot|woff2?)$/,
                    loader: require.resolve("file-loader"),
                    options: { name: "public/fonts/[hash].[ext]" },
                },
            ],
        },
        // @ts-expect-error Починится, когда все либы обновятся
        plugins: [
            new MiniCssExtractPlugin({
                filename: `public/styles/${gfv}${fileNameSuffix}.css`,
            }),
            new webpack.DefinePlugin({
                "process.env": {
                    GFV: JSON.stringify(gfv),
                    NODE_ENV: JSON.stringify(options.mode),
                    RESOURCES_HASH: JSON.stringify(options.resourcesHash),
                },
            }),
            buildCacheWebpackPlugin,
        ].filter(isNotNullOrUndefined),
    };
}

function isNotNullOrUndefined<T>(input: T | undefined | null): input is T {
    return input != undefined;
}

export interface CreateWebpackConfigurationOptions {
    gfv: string;
    resourcesHash: string;
    formPath: string;
    resourcesDirectoryPath: string;
    modes: BuildMode[];
    isLocalDev?: boolean;
    buildCacheOptions?: FormBuildCacheOptions;
}

export function createConfig(options: CreateWebpackConfigurationOptions): Promise<Configuration[]> {
    const result: Array<Promise<Configuration>> = [];

    for (const mode of options.modes) {
        result.push(
            createConfigVariant({
                gfv: options.gfv,
                resourcesHash: options.resourcesHash,
                formPath: options.formPath,
                resourcesDirectoryPath: options.resourcesDirectoryPath,
                mode: mode,
                isLocalDev: options.isLocalDev,
                buildCacheOptions: options.buildCacheOptions,
            })
        );
    }
    return Promise.all(result);
}
